import { createUserWithEmailAndPassword } from 'firebase/auth';
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components_catalyst/button';
import { Field, Label, ErrorMessage } from '../components_catalyst/fieldset';
import { Input } from '../components_catalyst/input';
import { auth } from '../firebase';
import { GoogleAuthButton } from './GoogleAuthButton';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store/store';
import { fetchSignUpLink } from '../store/map3DataSlice';
import { SaveAndRefresh } from '../components_misc/SaveAndRefresh';

export default function SignUp() {
  const [searchParams] = useSearchParams();
  const signUpCode = searchParams.get('signUpCode');
  const signUpLink = useSelector((state: RootState) =>
    signUpCode ? state.map3Data.signUpLink : null,
  );

  const dispatch = useDispatch<AppDispatch>();
  const reduxError = useSelector((state: RootState) => state.map3Data.error);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoadingLocal, setIsLoadingLocal] = useState(false);
  const navigate = useNavigate();

  // Validate signup code if present
  useEffect(() => {
    if (signUpCode) {
      dispatch(fetchSignUpLink(signUpCode));
    }
  }, [dispatch, signUpCode]);

  // Update error state when Redux error changes
  useEffect(() => {
    if (reduxError) {
      setError(reduxError);
    }
  }, [reduxError]);

  // Update email when signup link changes
  useEffect(() => {
    if (signUpLink?.toEmail) {
      setEmail(signUpLink.toEmail);
    }
  }, [signUpLink]);

  // Determine if the form should be disabled
  const isFormDisabled = Boolean(signUpCode && (!signUpLink || signUpLink?.status !== 'pending'));
  // If there's a valid signup link, don't let the user change the email they
  // use to register.
  const isEmailFieldDisabled = Boolean(signUpCode);
  const hasSignUpLinkError = Boolean(
    signUpCode && (!signUpLink || signUpLink?.status !== 'pending'),
  );

  const validateSignupEmail = (userEmail: string) => {
    if (signUpLink && userEmail !== signUpLink.toEmail) {
      setError('Please use the email address this signup link was created for');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoadingLocal(true);

    try {
      if (!validateSignupEmail(email)) {
        setIsLoadingLocal(false);
        return;
      }

      await createUserWithEmailAndPassword(auth, email, password);
      navigateToCompleteSignup();
    } catch (err: any) {
      setError(err.message || 'Failed to create account');
      setIsLoadingLocal(false);
    }
  };

  const navigateToCompleteSignup = () => {
    const searchParams = new URLSearchParams();
    if (signUpCode) {
      searchParams.set('signUpCode', signUpCode);
    }
    navigate(`/complete-signup`);
  };

  const handleGoogleError = (error: Error) => {
    setError(error.message);
  };

  const handleGoogleSuccess = async (userEmail: string) => {
    if (!validateSignupEmail(userEmail)) {
      return;
    }
    navigateToCompleteSignup();
  };

  return (
    <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
      <SaveAndRefresh hidden />

      <div className="sm:mx-auto sm:w-full sm:max-w-sm">
        <h2 className="text-center text-2xl font-bold leading-9 tracking-tight text-gray-900">
          Sign up
        </h2>
      </div>

      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
        {error && (
          <Field role="alert" aria-live="assertive" className="mb-4 text-center">
            <ErrorMessage>{error}</ErrorMessage>
          </Field>
        )}

        {hasSignUpLinkError && (
          <Field role="alert" aria-live="assertive" className="mb-4" id="signup-link-error">
            <ErrorMessage>
              This signup link is invalid or has already been used. Please request a new signup link
              or sign up with a different method.
            </ErrorMessage>
          </Field>
        )}

        <form className="space-y-6" onSubmit={handleSubmit} aria-label="Sign up form" noValidate>
          <Field>
            <Label htmlFor="email">Email address</Label>
            <Input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isEmailFieldDisabled}
              invalid={hasSignUpLinkError}
              aria-required="true"
              aria-invalid={hasSignUpLinkError}
              aria-describedby={hasSignUpLinkError ? 'signup-link-error' : undefined}
            />
          </Field>

          <Field>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              name="password"
              type="password"
              autoComplete="new-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isFormDisabled}
              invalid={hasSignUpLinkError}
              aria-required="true"
              aria-invalid={hasSignUpLinkError}
              aria-describedby={hasSignUpLinkError ? 'signup-link-error' : undefined}
              minLength={8}
              aria-label="Create a password"
            />
          </Field>

          <div>
            <Button
              type="submit"
              className="w-full"
              disabled={isFormDisabled}
              aria-disabled={isFormDisabled}
              aria-busy={isLoadingLocal}
            >
              {isLoadingLocal ? 'Signing up...' : 'Sign up'}
            </Button>
          </div>
        </form>

        <div className="relative my-6" role="separator" aria-orientation="horizontal">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500" aria-hidden="true">
              or
            </span>
          </div>
        </div>

        <div>
          <GoogleAuthButton
            mode="signup"
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
            disabled={isFormDisabled}
          />
        </div>
      </div>
    </div>
  );
}
